import { useContext, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { XCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import { formatter } from '../../../../../_utils/format';
import { DebtsType, DebtsSchema } from '../../types';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function Debts() {
	const financialContext = useContext(FinancialStatusContext);
	const {
		handleSubmit,
		control,
		watch,
		setValue,
		clearErrors,
		formState: { errors },
	} = useForm<DebtsType>({
		defaultValues: {
			hasDebts: false,
			debts: [],
			totalAmountOwed: 0,
		},
		shouldUnregister: false,
		resolver: zodResolver(DebtsSchema),
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'debts',
	});

	const hasDebts = watch('hasDebts');
	const debts = watch('debts', []); // Default value provided
	const totalAmountOwed = watch('totalAmountOwed', 0);

	useEffect(() => {
		if (debts.length === 0 && hasDebts) {
			// If there are no debts but hasDebts is true, reset hasDebts
			setValue('hasDebts', false);
			clearErrors('debts');
		} else if (debts.length > 0 && !hasDebts) {
			// If there are debts but hasDebts is false, set it to true
			setValue('hasDebts', true);
		}
		updateTotalAmountOwed();
	}, [debts, hasDebts, setValue, clearErrors]);

	const updateTotalAmountOwed = () => {
		const amountOwed = debts.reduce((acc, debt) => acc + (debt.amountOwed || 0), 0);
		setValue('totalAmountOwed', amountOwed);
	};
	const onSubmit = (data: DebtsType) => {
		financialContext.updateDebts(data);
		financialContext.updateCurrentStep(7);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={6} />
				<div className="mt-5 flex min-h-24 flex-col space-y-5">
					{hasDebts ? (
						fields.map((field, index) => (
							<div
								className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-10 lg:space-y-0"
								key={field.id}
							>
								<div className="flex min-w-60 flex-col">
									<Label currentStep="debts" value="amountOwed" />
									<Controller
										control={control}
										name={`debts.${index}.amountOwed`}
										render={({ field: fieldProps }) => (
											<input
												{...fieldProps}
												className={`${styles.mediumInput} ${errors.debts?.[index]?.amountOwed ? styles.error : ''}`}
												id={`debts.${index}.amountOwed`}
												onBlur={() => {
													updateTotalAmountOwed();
												}}
												onChange={(e) => fieldProps.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
												placeholder={financialContext.getPlaceholder({ step: 'debts', value: 'amountOwed' })}
												type="number"
												value={fieldProps.value === 0 ? '' : fieldProps.value}
											/>
										)}
									/>
									{errors.debts?.[index]?.amountOwed ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="debts" value="amountOwed" />
										</div>
									) : null}
								</div>
								<div className="flex min-w-60 flex-col">
									<Label currentStep="debts" value="monthlyPayment" />
									<Controller
										control={control}
										name={`debts.${index}.monthlyPayment`}
										render={({ field: fieldProps }) => (
											<input
												{...fieldProps}
												className={`${styles.mediumInput} ${errors.debts?.[index]?.monthlyPayment ? styles.error : ''}`}
												id={`debts.${index}.monthlyPayment`}
												onChange={(e) => fieldProps.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
												placeholder="Monthly Payment"
												type="number"
												value={fieldProps.value === 0 ? '' : fieldProps.value}
											/>
										)}
									/>
									{errors.debts?.[index]?.monthlyPayment ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="debts" value="monthlyPayment" />
										</div>
									) : null}
								</div>
								<div className="flex min-w-60 flex-col">
									<Label currentStep="debts" value="collateral" />
									<Controller
										control={control}
										name={`debts.${index}.collateral`}
										render={({ field: fieldProps }) => (
											<input
												{...fieldProps}
												className={`${styles.mediumInput} ${errors.debts?.[index]?.collateral ? styles.error : ''}`}
												id={`debts.${index}.collateral`}
												placeholder="Collateral"
												type="text"
											/>
										)}
									/>
									{errors.debts?.[index]?.collateral ? (
										<div className="md:max-w-56">
											<ErrorMessage currentStep="debts" value="collateral" />
										</div>
									) : null}
								</div>
								<div className="flex min-w-12 flex-col">
									<button onClick={() => remove(index)} type="button">
										<XCircleIcon className="h-8 w-8 text-red-700 lg:mt-8" />
									</button>
								</div>
							</div>
						))
					) : (
						<div className="min-h-24 md:w-fit"> </div>
					)}
				</div>
				<div className="min-h-24 md:w-fit">
					<button
						className="mt-2 flex items-center space-x-3"
						onClick={() => {
							if (!hasDebts) {
								setValue('hasDebts', true);
							}
							append({ amountOwed: 0, monthlyPayment: 0, collateral: '' });
						}}
						type="button"
					>
						<div>{financialContext.language === 'en' ? 'Add Debt' : 'Agregar deuda'}</div>
						<PlusCircleIcon className="h-8 w-8 text-green-800" />
					</button>
				</div>

				<div className="mb-10 flex justify-end space-y-3 md:min-h-24 md:w-3/4">
					<div className="w-full justify-between font-light text-gray-500 lg:flex lg:w-1/2">
						<div>Total estimated debts:</div>
						<div className="font-semibold">{formatter.format(totalAmountOwed)}</div>
					</div>
				</div>

				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(5);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
